import Vue from 'vue'
import { MessageBox } from 'element-ui'
import { formatMoney } from './money'

const COUPON_PRICE_CONFLICT_CODE = 40901
const CONFLICT_PAGE_SIZE = 50
const ALLOWED_ROUTE_NAMES = ['couponDetail', 'shopEdit', 'giftEdit']
const renderContext = new Vue()
let conflictMessageOpen = false

function buildSceneDescription (data) {
  if (data.scene === 'GOODS_SAVE') {
    return `当前商品：${data.goodsName || '-'} `
  }
  if (data.scene === 'GIFT_PACKAGE_SAVE') {
    return `当前礼包：${data.giftPackageName || '-'} `
  }
  if (data.scene === 'COUPON_ENABLE') {
    return `当前优惠券：${data.couponName || '-'}（面额 ${formatMoney(data.couponValue)}）`
  }
  return ''
}

export function isCouponPriceConflictResponse (response) {
  return Number(response && response.code) === COUPON_PRICE_CONFLICT_CODE &&
    response.data && Array.isArray(response.data.conflicts) && response.data.conflicts.some(Boolean)
}

export function buildCouponPriceConflictRoute (target) {
  const routeName = target && target.routeName
  if (ALLOWED_ROUTE_NAMES.indexOf(routeName) === -1 ||
    target.targetId === null || target.targetId === undefined) {
    return null
  }
  return {
    name: routeName,
    query: {
      id: target.targetId,
      type: target.routeType || 'edit'
    }
  }
}

function buildPriceNodes (h, target, copySku) {
  const items = Array.isArray(target.priceItems) ? target.priceItems : []
  return items.filter(Boolean).map((item, index) => {
    const children = []
    if (item.skuName) {
      children.push(h('span', `规格：${item.skuName}；`))
    }
    if (item.sku) {
      children.push(h('span', 'SKU：'))
      children.push(h('button', {
        class: 'coupon-price-conflict__sku',
        attrs: {
          type: 'button',
          title: '点击复制SKU'
        },
        on: {
          click: event => {
            event.preventDefault()
            event.stopPropagation()
            copySku(item.sku)
          }
        }
      }, item.sku))
      children.push(h('span', '；'))
    }
    children.push(h('span', `售价：${formatMoney(item.price)}`))
    if (item.afterCouponPrice !== null && item.afterCouponPrice !== undefined) {
      children.push(h('span', `；券后价：${formatMoney(item.afterCouponPrice)}`))
    }
    if (item.costPrice !== null && item.costPrice !== undefined) {
      children.push(h('span', `；成本价：${formatMoney(item.costPrice)}`))
    }
    if (item.lossAmount !== null && item.lossAmount !== undefined) {
      children.push(h('span', `；预计亏损：${formatMoney(item.lossAmount)}`))
    }
    return h('span', {
      key: `${item.sku || 'price'}_${index}`,
      class: 'coupon-price-conflict__price-item'
    }, children)
  })
}

function buildConflictItem (h, target, index, router, copySku) {
  const route = buildCouponPriceConflictRoute(target)
  const href = route ? router.resolve(route).href : ''
  const title = target.targetName || `异常记录${index + 1}`
  const detailNodes = []

  if (target.targetType !== 'COUPON') {
    detailNodes.push(h('span', `优惠券：${target.couponName || '-'}（面额 ${formatMoney(target.couponValue)}）`))
  } else {
    detailNodes.push(h('span', `优惠券面额：${formatMoney(target.couponValue)}`))
  }

  const priceNodes = buildPriceNodes(h, target, copySku)
  if (priceNodes.length) {
    detailNodes.push(...priceNodes)
  }

  return h('div', { class: 'coupon-price-conflict__item' }, [
    h('div', { class: 'coupon-price-conflict__name' }, [
      h('span', `${index + 1}. `),
      href
        ? h('a', {
          attrs: {
            href,
            target: '_blank',
            rel: 'noopener noreferrer'
          }
        }, title)
        : h('span', title)
    ]),
    h('div', { class: 'coupon-price-conflict__detail' }, detailNodes)
  ])
}

const CouponPriceConflictContent = {
  name: 'CouponPriceConflictContent',
  props: {
    conflictData: {
      type: Object,
      required: true
    },
    router: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      visibleCount: CONFLICT_PAGE_SIZE
    }
  },
  methods: {
    showMore () {
      this.visibleCount += CONFLICT_PAGE_SIZE
    },
    copySku (sku) {
      this.$copyText(String(sku)).then(
        () => this.$message.success('SKU复制成功'),
        () => this.$message.error('SKU复制失败，请手动复制')
      )
    }
  },
  render (h) {
    const conflicts = this.conflictData.conflicts.filter(Boolean)
    const visibleConflicts = conflicts.slice(0, this.visibleCount)
    const remainingCount = conflicts.length - visibleConflicts.length
    const children = [
      h('div', { class: 'coupon-price-conflict__description' }, buildSceneDescription(this.conflictData)),
      h('div', { class: 'coupon-price-conflict__tip' }, `以下 ${conflicts.length} 条记录使用优惠券后低于成本价，点击名称可在新页面处理：`),
      ...visibleConflicts.map((target, index) => buildConflictItem(h, target, index, this.router, this.copySku))
    ]

    if (remainingCount > 0) {
      children.push(h('button', {
        class: 'coupon-price-conflict__more',
        attrs: { type: 'button' },
        on: { click: this.showMore }
      }, `加载更多（剩余 ${remainingCount} 条）`))
    }
    return h('div', { class: 'coupon-price-conflict' }, children)
  }
}

export function showCouponPriceConflict (response, router) {
  if (!isCouponPriceConflictResponse(response)) {
    return false
  }
  if (conflictMessageOpen) {
    return true
  }

  const data = response.data
  const content = renderContext.$createElement(CouponPriceConflictContent, {
    props: { conflictData: data, router }
  })

  conflictMessageOpen = true
  const messagePromise = MessageBox({
    title: data.title || '优惠券价格校验未通过',
    message: content,
    customClass: 'coupon-price-conflict-message-box',
    confirmButtonText: '关闭',
    closeOnClickModal: false,
    showCancelButton: false
  })
  const resetOpenState = () => {
    conflictMessageOpen = false
  }
  messagePromise.then(resetOpenState, resetOpenState)
  return true
}
